import { EMPTY_OBJECT, EMPTY_STRING } from '../../constants/common'
import { EMAIL_REPLY_FORM_HTML_BLOCK_ID } from '../../constants/email'
import {
  HTML_OPERATOR_EQUALS,
  HTML_OPERATOR_STARTS_WITH,
} from '../../constants/html'
import {
  SIGNATURE_ATTRIBUTE,
  SIGNATURE_PREFIX,
  SIGNATURE_SEPARATOR,
  SIGNATURE_TAG,
} from '../../constants/signature'
import { getAttributeSelector, toHTML, toHTMLElement } from '../html'

function isSeparatorNode(node) {
  if (!node) {
    return false
  }

  if (isIgnorableTextNode(node)) {
    return false
  }

  if (node.nodeType !== Node.ELEMENT_NODE) {
    return false
  }

  const tag = node.tagName.toLowerCase()

  if (tag === 'br') {
    return true
  }

  if (tag === 'p') {
    const html = (node.innerHTML || EMPTY_STRING)
      .replace(/\s+/g, ' ')
      .replace(/&nbsp;/g, '\u00A0')
      .trim()

    // <p>&nbsp;</p> or empty <p> / <p> </p>
    if (html === EMPTY_STRING || html === '\u00A0') {
      return true
    }

    // <p><br></p>
    if (/^<br\s*\/?>$/i.test(html)) {
      return true
    }
  }

  return false
}

function isIgnorableTextNode(node) {
  return node?.nodeType === Node.TEXT_NODE && !node.textContent?.trim()
}

function prevMeaningfulSibling(node) {
  let cur = node?.previousSibling

  while (cur && isIgnorableTextNode(cur)) {
    cur = cur.previousSibling
  }

  return cur
}

function lastMeaningfulChild(parent) {
  let cur = parent?.lastChild

  while (cur && isIgnorableTextNode(cur)) {
    cur = cur.previousSibling
  }

  return cur
}

/**
 * @param {HTMLElement} textElement
 * @param {string | null} signatureSelector
 *
 * @returns {HTMLElement | null}
 */
export function querySignature(textElement, signatureSelector) {
  return signatureSelector ? textElement.querySelector(signatureSelector) : null
}

/**
 * @param {number | null} id
 */
export function getSignatureAttributeValue(id = null) {
  return `${SIGNATURE_PREFIX}${id || EMPTY_STRING}`
}

/**
 * @param {number | null} signatureId
 * @returns {string}
 */
export function getSignatureSelector(signatureId = null) {
  return getAttributeSelector(
    SIGNATURE_ATTRIBUTE,
    getSignatureAttributeValue(signatureId),
    signatureId ? HTML_OPERATOR_EQUALS : HTML_OPERATOR_STARTS_WITH
  )
}

/**
 * @param {number} id
 * @param {string} message
 * @param {string} [tag='div']
 *
 * @returns {string}
 */
export function createSignature(id, message, tag = SIGNATURE_TAG) {
  let element = null

  if (message) {
    element = document.createElement(tag)

    element.innerHTML = toHTML(message)
    setSignatureAttribute(element, id)
  }

  // .replace(/></g, '>\n<') added for compatibility with tinyMCE
  return element?.outerHTML?.replace(/></g, '>\n<') ?? EMPTY_STRING
}

/**
 * @param {HTMLElement} element
 * @param {number} id
 */
export function setSignatureAttribute(element, id) {
  element.setAttribute(SIGNATURE_ATTRIBUTE, getSignatureAttributeValue(id))
}

/**
 * @param {string} message
 * @param {number | null} signatureId
 *
 * @returns {string}
 */
export function findSignature(message, signatureId) {
  const textElement = toHTMLElement(message)
  const selector = getSignatureSelector(signatureId)

  return querySignature(textElement, selector)?.outerHTML ?? EMPTY_STRING
}

/**
 * @param {string} message
 * @param {number | null} signatureId
 *
 * @returns {boolean}
 */
export function containsSignature(message, signatureId) {
  return findSignature(message, signatureId) !== EMPTY_STRING
}

/**
 * @param {object} options
 *
 * @param {string} options.message
 * @param {string} options.signatureHTML
 * @param {boolean} options.insertSignatureBeforeQuotedText
 *
 * @returns {string}
 */
export function addSignature({
  insertSignatureBeforeQuotedText,
  message,
  signatureHTML,
}) {
  const textElement = toHTMLElement(message)

  if (!signatureHTML) {
    return textElement.innerHTML
  }

  if (insertSignatureBeforeQuotedText) {
    const replyNode = textElement.querySelector(
      `#${EMAIL_REPLY_FORM_HTML_BLOCK_ID}`
    )

    if (replyNode?.parentNode) {
      const prev = prevMeaningfulSibling(replyNode)

      if (isSeparatorNode(prev)) {
        prev.parentNode.insertBefore(toHTMLElement(signatureHTML), prev)
      } else {
        replyNode.parentNode.insertBefore(
          toHTMLElement(signatureHTML + SIGNATURE_SEPARATOR),
          replyNode
        )
      }

      return textElement.innerHTML.replace(/></g, '>\n<')
    }
  }

  const last = lastMeaningfulChild(textElement)

  if (isSeparatorNode(last)) {
    last.parentNode.insertBefore(toHTMLElement(signatureHTML), last)
  } else {
    textElement.appendChild(toHTMLElement(SIGNATURE_SEPARATOR + signatureHTML))
  }

  return textElement.innerHTML.replace(/></g, '>\n<')
}

/**
 * @param {string} message
 *
 * @returns {string}
 */
export function removeSignature(message) {
  const textElement = toHTMLElement(message)
  const signatureElement = querySignature(textElement, getSignatureSelector())

  let nextText = message

  if (signatureElement) {
    signatureElement.remove()
    nextText = textElement.innerHTML

    if (nextText.trim() === SIGNATURE_SEPARATOR) {
      nextText = EMPTY_STRING
    }
  }

  return nextText
}

/**
 * @param {string} message
 * @param {?object} account
 *
 * @returns {[string, string]}
 */
export function updateSignature(message, account) {
  const {
    insert_signature_before_quoted_text: insertSignatureBeforeQuotedText,
    signature: accountSignature,
    value: accountId,
  } = account || EMPTY_OBJECT

  let nextMessage = message
  let signatureHTML = findSignature(message, accountId)

  if (accountId && signatureHTML === EMPTY_STRING) {
    signatureHTML = createSignature(accountId, accountSignature)

    nextMessage = removeSignature(nextMessage)
    nextMessage = addSignature({
      insertSignatureBeforeQuotedText,
      message: nextMessage,
      signatureHTML,
    })
  } else if (!accountId || !accountSignature) {
    nextMessage = removeSignature(message)
  }

  return [nextMessage, signatureHTML]
}
